import { clearCachedReferenceData } from '@/lib/offline-store';

/**
 * Durable caches that outlive the page — and therefore would outlive a
 * session unless explicitly purged at logout.
 *
 * The React Query cache lives in memory, so a full page reload disposes
 * of it. These two do not:
 *
 *  1. IndexedDB `cached-data` — reference data snapshotted by
 *     `useOfflineCache` under `ref:<tenant>:<kind>` and replayed for 24 h
 *     whenever the app goes offline. Since DB v3 the tenant is part of
 *     the key, so the next session cannot read this one's snapshots even
 *     if this purge never runs — which is exactly what an unclean exit
 *     (killed tab, crash, power cut) produces. Purging here is therefore
 *     defence in depth, and it still earns its place: an orderly logout
 *     leaves no stale copy of server data on the device.
 *
 *  2. CacheStorage `api-cache` — `src/sw.ts` does not write it; a copy
 *     may exist from an older service worker, and deleting a missing
 *     cache is a no-op.
 *
 * Every step is best-effort and independently guarded: a browser that
 * denies storage (private mode, quota) must not be able to block logout.
 */

/** Name of the runtime cache declared in `src/sw.ts`. */
export const API_RESPONSE_CACHE_NAME = 'api-cache';

async function purgeReferenceData(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    await clearCachedReferenceData();
  } catch {
    // Storage unavailable — nothing cached, nothing to leak.
  }
}

async function purgeApiResponseCache(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    await caches.delete(API_RESPONSE_CACHE_NAME);
  } catch {
    // CacheStorage unavailable (no service worker, insecure context).
  }
}

/**
 * Drop every durable cache that may hold data of the session being
 * closed. Called from `AuthContext.logout()` — the single funnel every
 * logout entry point goes through.
 */
export async function purgeSessionCaches(): Promise<void> {
  await Promise.all([purgeReferenceData(), purgeApiResponseCache()]);
}
